import type { Artifact } from '@shared/types/artifacts'
import type { PackageJsonESM } from '@shared/wippy-package/wippy-package'
import type { AxiosInstance } from 'axios'
import type { TagAllowlistTracker } from './tag-allowlist-tracker.ts'
import { getApiRoutes, resolveRoute } from '@shared/api/routes'
import { importESMComponentByPackageJSON } from '@shared/api/web-components'
import { makeArtifactApi } from '@shared/api/web-components/make-artifact-api'
import { trackLoad, whenDefinedWithTimeout } from '@shared/api/web-components/wc-load-tracker'
import { isWippyPackageESMModule } from '@shared/wippy-package/wippy-package'
import { extractPropAttrs } from './sanitize.ts'

export async function loadWebComponent(
  api: AxiosInstance,
  componentId: string,
  tagName?: string,
  tracker?: TagAllowlistTracker,
): Promise<void> {
  const loader = makeArtifactApi<Artifact>(api)
  const meta = await loader.getMeta(componentId)
  if (meta.content_type !== 'application/json') {
    throw new Error(`Artifact ${componentId} is not a valid web component`)
  }
  const content = await loader.getContent(componentId)
  if (!content || typeof content.data === 'string') {
    throw new Error(`Artifact ${componentId} is html page, not a web component`)
  }
  if (!isWippyPackageESMModule(content.data)) {
    throw new Error(`Artifact ${componentId} is not a valid Wippy package ESM module`)
  }
  await importESMComponentByPackageJSON({
    packageJson: content.data,
    uuid: componentId,
  }, tagName, api)
  // Allowlist the registered tag AFTER `whenDefined` resolves — matches
  // `loadByTagName`'s post-define ordering so a failed-to-evaluate
  // script (404, parse error, missing `customElements.define`) doesn't
  // leak a tag into the sanitize allowlist. `parseDomForLoaders` wraps
  // this call in `trackLoad` which calls `whenDefinedWithTimeout`
  // separately; the direct `$W.loadWebComponent(...)` path doesn't, so
  // we await here too for consistent semantics across both entrypoints.
  const finalTag = tagName || content.data.wippy?.tagName
  if (finalTag) {
    await whenDefinedWithTimeout(finalTag)
    tracker?.add(finalTag, extractPropAttrs(content.data.wippy?.props))
  }
}

interface FindByTagResponse {
  success: boolean
  error?: string
  // The views API returns a camelCase wippy-component-1.0 descriptor (a valid
  // PackageJsonESM) plus the registry `id` — consumed directly, no re-assembly.
  component?: PackageJsonESM & { id: string }
}

export interface LoadByTagNameOptions {
  /**
   * How long to wait for `customElements.define(tag, ...)` after the
   * `<script>` is appended before rejecting. Default
   * `DEFAULT_WHEN_DEFINED_TIMEOUT_MS` (30s) — surfaces a stuck or broken
   * WC (404, parse error, missing define call) as a rejection instead
   * of an indefinite hang.
   *
   * Only honoured on the FIRST concurrent caller — if a load for this
   * tag is already in flight, the existing promise is returned and this
   * value is ignored (factory is not re-invoked).
   */
  timeoutMs?: number
}

/**
 * Load a registered `view.component` by its custom-element tag name and
 * register it with `customElements.define()`. Resolves via the views API
 * (`GET /components/by-tag/{tag}`) so the consumer does not need to know
 * the peer module's URL or registry id.
 *
 * The returned promise resolves once the element class has been defined
 * (so `customElements.whenDefined(tag)` resolves immediately after). Throws
 * if the tag is not registered, the response is malformed, the import
 * fails, or `customElements.define` doesn't fire within `options.timeoutMs`.
 *
 * **Concurrency:** all load paths in this bundle route through the
 * tag-keyed `trackLoad` cache, so N concurrent calls to load the same tag
 * — including a `loadByTagName(t)` racing against a `loadGlobalAutoloadWidgets`
 * registration for `t` or a `<wippy-components-loader>` DOM-scan entry —
 * share one in-flight promise. Exactly one API request, one `<script>`
 * tag, one `customElements.define()`. Already-defined tags resolve
 * immediately without an API call.
 */
export async function loadByTagName(
  api: AxiosInstance,
  tag: string,
  options?: LoadByTagNameOptions,
  tracker?: TagAllowlistTracker,
): Promise<void> {
  // Normalize and validate. `customElements.define` rejects whitespace
  // (per the HTML spec the name must contain a hyphen and only ASCII), so
  // a tag like `'  w-foo  '` would fail deep in the chain with a confusing
  // `SyntaxError`. Trim once at the boundary and use the trimmed value
  // everywhere downstream — the tracker key, the API URL, and the
  // `whenDefined` lookup all see the same normalized string.
  const normalizedTag = tag?.trim()
  if (!normalizedTag) {
    throw new Error('loadByTagName: tag is required')
  }

  return trackLoad(normalizedTag, async () => {
    // Route comes from the host's api-routes config (overridable per deployment),
    // so the consumer doesn't need to know which prefix this Wippy instance
    // mounts the views API on. See `@shared/api/routes` for the default.
    // Custom-element tag names per HTML spec are kebab-case + lowercase, but
    // defensively encode in case a deployment uses something exotic.
    const route = resolveRoute(getApiRoutes().components.byTag, { tag: encodeURIComponent(normalizedTag) })
    const res = await api.get<FindByTagResponse>(route)
    if (!res.data?.success || !res.data.component) {
      throw new Error(`loadByTagName(${normalizedTag}): ${res.data?.error || 'not found'}`)
    }
    const c = res.data.component
    if (!c.baseUrl || !c.browser) {
      throw new Error(`loadByTagName(${normalizedTag}): registry entry missing baseUrl or browser`)
    }

    // The descriptor IS a PackageJsonESM — hand it straight to the import
    // pipeline, no re-assembly (symmetric with `loadWebComponent`'s
    // `content.data` pass-through). `baseUrl` short-circuits the artifact-API
    // fallback in `getWebComponentStartUrl()`, which joins it to the entry via
    // `joinUrl` (one slash regardless of the BE's trailing slash). The
    // `normalizedTag` arg is the fallback when the descriptor omits a tag.
    await importESMComponentByPackageJSON({
      packageJson: c,
      uuid: `tag:${normalizedTag}`,
    }, normalizedTag, api)
    // `importESMComponentByPackageJSON` returns after appending the
    // `<script type="module">` to `document.head` — it does NOT await
    // module evaluation. The actual `customElements.define(tag, ...)`
    // happens whenever the browser finishes fetching + evaluating the
    // module. Wait for that here (with a hard deadline to surface
    // permanently broken WCs as a rejection instead of an indefinite
    // hang) so the promise's contract holds: resolving means callers
    // can immediately `document.createElement(tag)` and get the real
    // WC instead of an `HTMLUnknownElement` placeholder.
    //
    // The dedup is preserved — this `await` runs inside the `trackLoad`
    // factory, so concurrent `loadByTagName(tag)` callers still share
    // the single in-flight promise. They just all resolve at the same
    // moment, after `define` has fired.
    await whenDefinedWithTimeout(normalizedTag, options?.timeoutMs)

    // Allowlist the registered tag + its declared attrs in the current
    // proxy context's sanitizer tracker. Runs only on successful
    // registration (after `whenDefined`) so failed loads don't leak
    // entries. No-op when no tracker is wired.
    tracker?.add(normalizedTag, extractPropAttrs(c.wippy?.props))
  })
}

export async function parseDomForLoaders(
  api: AxiosInstance,
  dom: Document,
  tracker?: TagAllowlistTracker,
): Promise<void> {
  const loaders = dom.querySelectorAll('script[type="wippy-components-loader"]')
  for (const loader of loaders) {
    try {
      /**
       * JSON is map of tags to component IDs
       */
      const json = JSON.parse(loader.innerHTML || '{}')
      const allTags = Object.keys(json)
      allTags.forEach((tagName) => {
        const componentId = json[tagName]
        if (componentId) {
          // Route through `trackLoad` so this scan's load shares its
          // promise with any concurrent `$W.loadByTagName(tagName)` /
          // autoload registration for the same tag — see wc-load-tracker.
          // `loadWebComponent` itself awaits `whenDefinedWithTimeout`
          // before pushing into the tracker, so the shared promise
          // resolves once the tag is actually defined.
          trackLoad(tagName, () => loadWebComponent(api, componentId, tagName, tracker))
            .catch((e) => {
              console.error(`Failed to load web component ${tagName} (${componentId}):`, e)
            })
        }
      })
    }
    catch (e) {
      console.error(`Failed to parse web component loader JSON:`, e, loader)
    }
  }
}
